import React, { useState, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { educationAPI } from "../api/axiosClient";
import { LoginRequest } from "../api/types";

interface TFAFormProps {
  onSuccess: (token: string) => void;
  onBack: () => void;
  username: string;
  password: string;
  onLoginSuccess: () => void;
}

const TFAForm: React.FC<TFAFormProps> = ({
  onSuccess,
  onBack,
  username,
  password,
  onLoginSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tfaCode, setTfaCode] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const clearError = () => {
    setError(null);
  };

  // Focus first input on mount
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleInputChange = (index: number, value: string) => {
    if (value.length > 1) return; // Prevent multiple characters

    const newCode = [...tfaCode];
    newCode[index] = value;
    setTfaCode(newCode);

    // Clear error when user types
    if (error) {
      clearError();
    }

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !tfaCode[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const code = tfaCode.join("");
    if (code.length !== 6) {
      return;
    }

    try {
      // Use the same login endpoint with twoFactorCode
      const loginRequest: LoginRequest = {
        username,
        password,
        twoFactorCode: code,
      };

      console.log("TFA verification attempt:", {
        username,
        password: password ? "***" : "undefined",
        twoFactorCode: code,
      });

      setLoading(true);
      setError(null);
      const response = await educationAPI.login(loginRequest);

      if (response?.success && response.data?.token) {
        // Store all tokens and user data
        localStorage.setItem("authToken", response.data.token);

        if (response.data.refreshToken) {
          localStorage.setItem("refreshToken", response.data.refreshToken);
        }

        if (response.data.refreshTokenExpiresAt) {
          localStorage.setItem(
            "refreshTokenExpiresAt",
            response.data.refreshTokenExpiresAt,
          );
        }

        if (response.data.userId) {
          localStorage.setItem("userId", response.data.userId);
        }

        // Call success callback
        onSuccess(response.data.token);

        // Call login success to redirect to dashboard
        onLoginSuccess();

        console.log("TFA verification successful, tokens stored:", {
          token: response.data.token ? "***" : "undefined",
          refreshToken: response.data.refreshToken ? "***" : "undefined",
          userId: response.data.userId,
          expiresAt: response.data.refreshTokenExpiresAt,
        });
      } else {
        console.error("TFA verification failed:", response?.message);
      }
    } catch (err) {
      console.error("TFA verification error:", err);
      setError(err instanceof Error ? err.message : "TFA verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      // Resend TFA code using the same login endpoint without twoFactorCode
      const loginRequest: LoginRequest = {
        username,
        password,
        // No twoFactorCode - this will trigger a new TFA code generation
      };

      setLoading(true);
      setError(null);
      const response = await educationAPI.login(loginRequest);

      if (response?.success && response.data?.requiresTwoFactor) {
        console.log("TFA code resent successfully");
        // Clear the current TFA code input
        setTfaCode(["", "", "", "", "", ""]);
        // Focus first input
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }
      } else {
        console.error("Failed to resend TFA code:", response?.message);
      }
    } catch (err) {
      console.error("Failed to resend TFA code:", err);
      setError(
        err instanceof Error ? err.message : "Failed to resend TFA code",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Sol Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#9B51E0] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-10 w-32 h-32 bg-white/10 rounded-full"></div>
          <div className="absolute top-40 right-20 w-48 h-48 bg-white/5 rounded-full"></div>
          <div className="absolute bottom-32 left-20 w-40 h-40 bg-white/10 rounded-full"></div>
          <div className="absolute bottom-20 right-10 w-24 h-24 bg-white/5 rounded-full"></div>
        </div>

        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="mb-8">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
              <svg
                className="w-10 h-10 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <h1 className="text-3xl font-bold mb-4">Güvenlik Doğrulaması</h1>
            <p className="text-lg text-white/80">
              Hesabınızı korumak için iki faktörlü doğrulama kullanıyoruz
            </p>
          </div>

          <div className="space-y-4 mt-8">
            <div className="flex items-center space-x-3 text-white/80">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <span>SMS ile gönderilen 6 haneli kod</span>
            </div>
            <div className="flex items-center space-x-3 text-white/80">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <span>Kod 5 dakika geçerlidir</span>
            </div>
          </div>
        </div>
      </div>

      {/* Sağ Panel - TFA Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-[#9B51E0] rounded-2xl mb-4">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
          </div>

          <div className="mb-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900">
              İki Faktörlü Doğrulama
            </h2>
            <p className="text-gray-500 mt-2">
              Telefonunuza gönderilen 6 haneli kodu girin
            </p>
          </div>

          <Card className="border-0 shadow-xl bg-white">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="p-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg flex items-start">
                    <svg
                      className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {error}
                  </div>
                )}

                <div className="space-y-4">
                  <Label className="text-sm font-medium text-gray-700 text-center block">
                    Doğrulama Kodu
                  </Label>

                  <div className="flex justify-center space-x-3">
                    {tfaCode.map((digit, index) => (
                      <Input
                        key={index}
                        ref={(el) => {
                          inputRefs.current[index] = el;
                        }}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={1}
                        value={digit}
                        onChange={(e) =>
                          handleInputChange(index, e.target.value)
                        }
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        className="w-12 h-14 text-center text-xl font-bold border-gray-200 focus:ring-2 focus:ring-[#9B51E0]/20 focus:border-[#9B51E0] rounded-xl"
                        disabled={loading}
                      />
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <Button
                    type="submit"
                    className="w-full h-11 bg-[#9B51E0] hover:bg-[#8B41D0] text-white font-medium rounded-lg transition-all duration-200 shadow-lg shadow-[#9B51E0]/25"
                    disabled={loading || tfaCode.join("").length !== 6}
                  >
                    {loading ? (
                      <span className="flex items-center justify-center">
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Doğrulanıyor...
                      </span>
                    ) : (
                      "Doğrula"
                    )}
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-11 border-gray-200 text-gray-700 hover:bg-gray-50 font-medium rounded-lg"
                    onClick={handleResendCode}
                    disabled={loading}
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    Kodu Tekrar Gönder
                  </Button>

                  <button
                    type="button"
                    className="w-full flex items-center justify-center text-gray-500 hover:text-[#9B51E0] font-medium transition-colors py-2"
                    onClick={onBack}
                    disabled={loading}
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 19l-7-7m0 0l7-7m-7 7h18"
                      />
                    </svg>
                    Geri Dön
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-gray-400 mt-8">
            © 2026 EduPayz. Tüm hakları saklıdır.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TFAForm;
